/* eslint-disable max-lines */
import {
  DatabaseAuto,
  IAnalyticsResultItem,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdeleteOneResponse,
  Ifile,
  IfileMeta,
  IfilterProps,
  Iitem,
  IpriceMeta,
  TitemInventoryRequest,
  TitemRequest,
  TitemState,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';
import { ItemDecoy, TitemDecoyProps } from './item-decoy.define';

/**
 * Represents a product or service item in the commerce system.
 * This class provides methods to manage items, including CRUD operations,
 * inventory management, and analytics.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TitemProps - The type definition for item properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Item} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const item = await Item.getAll();
 *
 * const item = await Item.filterAll({
 *
 * });
 *
 * const item = await Item.bulkDelete({
 *
 * });
 *
 * const item = await Item.getOne('resourceId');
 *
 * const item = await Item.remove();
 *
 * ```
 */
export class Item extends DatabaseAuto {
  /** Base API endpoint for item operations */
  private static readonly baseUrl = '/item';

  /** Unique reference ID for the item */
  readonly urId: Iitem['urId'];

  /** Company ID that owns the item */
  readonly company: Iitem['company'];

  /** Current stock quantity */
  readonly numbersInstock: Iitem['numbersInstock'];

  /** Name of the item */
  readonly name: Iitem['name'];

  /** Brand of the item */
  readonly brand?: Iitem['brand'];

  /** Main category of the item */
  readonly category?: Iitem['category'];

  /** Sub-category of the item */
  readonly subCategory?: Iitem['subCategory'];

  /** Condition/state of the item (new, used, refurbished) */
  readonly state?: Iitem['state'];

  /** Available colors for the item */
  readonly colors?: Iitem['colors'];

  /** Available sizes for the item */
  readonly sizes?: Iitem['sizes'];

  /** Model number/identifier */
  readonly itemModel?: string;

  /** Country/region of origin */
  readonly origin?: string;

  /** Pricing information and metadata */
  readonly priceMeta: IpriceMeta;

  /** Detailed description of the item */
  readonly description?: Iitem['description'];

  /** Array of product images */
  readonly photos: IfileMeta[] = [];

  /** Any known issues or problems with the item */
  readonly anyKnownProblems?: string;

  /** Promotion information */
  readonly promoted?: Iitem['promoted'];

  /** Warranty/guarantee information */
  readonly buyerGuarantee?: Iitem['buyerGuarantee'];

  /** List of users who reviewed the item */
  readonly reviewedBy?: Iitem['reviewedBy'];

  /** Total number of reviews */
  readonly reviewCount: Iitem['reviewCount'];

  /** Weight/importance of reviews */
  readonly reviewWeight: Iitem['reviewWeight'];

  /** List of users who liked the item */
  readonly likes?: Iitem['likes'];

  /** Total number of likes */
  readonly likesCount: Iitem['likesCount'];

  /** Number of times the item was viewed */
  readonly timesViewed: Iitem['timesViewed'];

  /** Quantity ordered (for cart/checkout) */
  readonly orderedQty: Iitem['orderedQty'];

  /** Sum of all review ratings */
  readonly reviewRatingsTotal: Iitem['reviewRatingsTotal'];

  /** E-commerce platform compatibility flag */
  readonly ecommerceCompat: Iitem['ecommerceCompat'];

  /** Total number of items sold */
  readonly soldCount: Iitem['soldCount'];

  /**
   * Creates a new instance of Item.
   * @param {Iitem} data - The item data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: Iitem) {
    const validStates: TitemState[] = ['new', 'used', 'refurbished'];

    validateData(data, {
      urId: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique reference ID must be a non-empty string'
      },
      company: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Company ID must be a non-empty string'
      },
      numbersInstock: {
        validate: (v) =>
          typeof v === 'number' &&
          v >= 0,
        message: 'Number in stock must be a non-negative number'
      },
      name: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Item name must be a non-empty string'
      },
      priceMeta: {
        validate: (v) =>
          v !== null &&
          typeof v === 'object' &&
          v !== null &&
          'sellingPrice' in v &&
          !isNaN(Number(v.sellingPrice)) &&
          Number(v.sellingPrice) >= 0,
        message: 'Price metadata must be a valid object with a non-negative selling price'
      },
      state: {
        required: false,
        validate: (v) =>
          v === undefined ||
          validStates.includes(v),
        message: `Item state must be one of: ${validStates.join(', ')}`
      },
      category: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Category must be a non-empty string if provided'
      }
    });

    super(data);

    // Assign and validate data
    this.urId = data.urId;
    this.company = data.company.toString();
    this.numbersInstock = data.numbersInstock;
    this.name = data.name;

    // Optional fields with default handling
    this.brand = data.brand || undefined;
    this.category = data.category || undefined;
    this.subCategory = data.subCategory || undefined;
    this.state = data.state || undefined;
    this.colors = data.colors || [];
    this.sizes = data.sizes || [];
    this.itemModel = data.itemModel || undefined;
    this.origin = data.origin || undefined;
    this.priceMeta = data.priceMeta;
    this.description = data.description || undefined;
    this.photos = data.photos || [];
    this.anyKnownProblems = data.anyKnownProblems || undefined;
    this.promoted = data.promoted || [];
    this.buyerGuarantee = data.buyerGuarantee || undefined;
    this.reviewedBy = data.reviewedBy || [];
    this.reviewCount = data.reviewCount || 0;
    this.reviewWeight = data.reviewWeight || 0;
    this.likes = data.likes || [];
    this.likesCount = data.likesCount || 0;
    this.timesViewed = data.timesViewed || 0;
    this.orderedQty = data.orderedQty || 1;
    this.reviewRatingsTotal = data.reviewRatingsTotal || 0;
    this.ecommerceCompat = Boolean(data.ecommerceCompat);
    this.soldCount = data.soldCount || 0;
  }

  /**
   * Retrieves analytics data for items within a specified date range.
   * @param {Date} startDate - The start date for the analytics period
   * @param {Date} endDate - The end date for the analytics period
   * @returns {Promise<IAnalyticsResultItem>} The analytics data
   * @throws {Error} If there's an error fetching the analytics
   */
  static getItemAnalytics = (startDate: Date, endDate: Date) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IAnalyticsResultItem>(
        `${Item.baseUrl}/analytics`,
        { startDate, endDate }
      );

    return lastValueFrom(observer$);
  };

  /**
   * Retrieves today's suggested items with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Item[] }>}
   * An object containing the total count and an array of suggested items
   * @throws {Error} If there's an error fetching the suggestions
   */
  static getTodaysSuggestions = async(offset = 0, limit = 20) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<Iitem>>(
        `${Item.baseUrl}/todays-suggestions`,
        { offset, limit }
      );

    const items = await lastValueFrom(observer$);

    return {
      count: items.count,
      dataArr: items.data.map(val => new Item(val))
    };
  };

  /**
   * Retrieves items being used as decoys for behavior analysis.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: ItemDecoy[] }>}
   * An object containing the total count and an array of decoy items
   * @throws {Error} If there's an error fetching the decoy items
   */
  static getBehaviourDecoy = async(offset = 0, limit = 20) => {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemDecoyProps>>(
        `${Item.baseUrl}/behaviour-decoy`,
        { offset, limit }
      );

    const items = await lastValueFrom(observer$);

    return {
      count: items.count,
      dataArr: items.data.map(val => new ItemDecoy(val))
    };
  };

  /**
   * Retrieves items based on filter criteria with pagination support.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Item[] }>}
   * An object containing the total count and an array of filtered items
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<Iitem>>(
        `${Item.baseUrl}/filter`,
        filter
      );

    const items = await lastValueFrom(observer$);

    return {
      count: items.count,
      dataArr: items.data.map(val => new Item(val))
    };
  }

  /**
   * Retrieves a single item by its ID.
   * @param {string} resourceId - The unique identifier of the item
   * @returns {Promise<Item>} The requested item
   * @throws {Error} If the item is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<Iitem>(`${Item.baseUrl}/${resourceId}`);
    const item = await lastValueFrom(observer$);

    return new Item(item);
  }

  /**
   * Creates a new item with associated inventory.
   * @param {Object} payload - The item and inventory data
   * @param {TitemRequest<true>} payload.item - The item data to create
   * @param {TitemInventoryRequest<true>} payload.itemInventory - The inventory data to create
   * @returns {Promise<Item>} The newly created item
   * @throws {Error} If the creation fails due to invalid data or server error
   */
  static async add(payload: { item: TitemRequest<true>; itemInventory: Omit<TitemInventoryRequest<true>, 'item'> }) {
    const observer$ = CommerceClient.ehttp
      .makePost<Iitem>(
        `${Item.baseUrl}`,
        payload
      );

    return new Item(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple items based on the specified criteria.
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk deletion operation
   * @throws {Error} If the bulk deletion fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Item.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Uploads images for the current item.
   * @param {Ifile[]} files - The image files to upload
   * @returns {Observable<Iitem>} Observable with the updated item
   * @throws {Error} If the upload fails or the item doesn't exist
   */
  uploadImages(files: Ifile[]) {
    return CommerceClient.ehttp
      .uploadFiles<Iitem>(
        files,
        `${Item.baseUrl}/upload-images`,
        { _id: this._id }
      );
  }

  /**
   * Updates the current item with new values.
   * @param {TitemRequest} payload - The updated item data
   * @returns {Promise<Item>} The updated item
   * @throws {Error} If the update operation fails or the item doesn't exist
   */
  async update(payload: TitemRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<Iitem>(
        `${Item.baseUrl}/${this._id}`,
        payload
      );

    return new Item(await lastValueFrom(observer$));
  }

  /**
   * Deletes the current item.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the deletion operation
   * @throws {Error} If the deletion fails or the item doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Item.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }

  /**
   * Adds a like to the current item from the current user.
   * @returns {Promise<Item>} The updated item with new like count
   * @throws {Error} If the operation fails or the item doesn't exist
   */
  async like() {
    const observer$ = CommerceClient.ehttp
      .makePost<Iitem>(
        `${Item.baseUrl}/like`,
        { itemId: this._id }
      );

    return new Item(await lastValueFrom(observer$));
  }

  /**
   * Removes a like from the current item for the current user.
   * @returns {Promise<Item>} The updated item with updated like count
   * @throws {Error} If the operation fails or the item doesn't exist
   */
  async unLike() {
    const observer$ = CommerceClient.ehttp
      .makePost<Iitem>(
        `${Item.baseUrl}/unlike`,
        { itemId: this._id }
      );

    return new Item(await lastValueFrom(observer$));
  }

  /**
   * Deletes images from the current item.
   * @param {string[]} fileIds - The IDs of the images to delete
   * @returns {Promise<Item>} The updated item with images removed
   * @throws {Error} If the deletion fails or the item doesn't exist
   */
  async deleteImages(fileIds: string[]) {
    const observer$ = CommerceClient.ehttp
      .makePost<Iitem>(
        `${Item.baseUrl}/delete-images`,
        { fileIds, _id: this._id }
      );

    return new Item(await lastValueFrom(observer$));
  }
}
